import { Link, useMatches } from '@tanstack/react-router'
import type { LinkProps } from '@tanstack/react-router'
import {
  BarChart3,
  BookOpen,
  ChevronsLeft,
  ChevronsRight,
  Home,
  LogOut,
  Navigation,
  Settings,
  ShieldCheck,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '~/lib/utils'
import { isNavLinkActive } from '~/lib/nav-active'
import { useAuth } from 'react-oidc-context'
import { useCurrentUser, usePermission } from '~/lib/current-user'
import { Separator } from '~/components/ui/separator'
import { Button } from '~/components/ui/button'
import { UserBadge } from '~/components/UserBadge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/components/ui/tooltip'

interface AppSidebarProps {
  isOpen: boolean
  onToggle: () => void
  collapsed: boolean
  onCollapse: (collapsed: boolean) => void
}

export function AppSidebar({
  isOpen,
  onToggle,
  collapsed,
  onCollapse,
}: Readonly<AppSidebarProps>) {
  const { user: currentUser } = useCurrentUser()
  const canAnalytics = usePermission('analytics:read')
  const canDispatch = usePermission('dispatch:read')

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-full flex-col border-r bg-sidebar text-sidebar-foreground transition-all lg:relative lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'w-14' : 'w-64',
        )}
      >
        {collapsed && (
          <button
            type="button"
            aria-label="Expand sidebar"
            className="absolute -right-4 top-4 z-20 hidden h-12 w-4 items-center justify-center rounded-r-md border border-l-0 bg-sidebar text-muted-foreground hover:text-foreground transition-colors lg:flex"
            onClick={() => onCollapse(false)}
          >
            <ChevronsRight className="h-3 w-3" />
          </button>
        )}

        <div className={cn('flex h-14 items-center px-3', !collapsed && 'justify-between')}>
          <div className={cn('flex items-center gap-2 min-w-0', collapsed && 'justify-center w-full')}>
            <Link to="/" aria-label="Home" className="shrink-0">
              <img src="/favicon.svg" alt="App" className="h-6 w-6" />
            </Link>
            {!collapsed && currentUser && (
              <div className="min-w-0">
                <Link to="/" className="font-semibold text-sm leading-tight block hover:underline">
                  First Response
                </Link>
                <Link
                  to="/settings"
                  className="mt-0.5 inline-flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <UserBadge name={currentUser.name} avatarUrl={currentUser.avatarUrl} size="sm" avatarOnly />
                  <span className="truncate">{currentUser.name}</span>
                </Link>
              </div>
            )}
          </div>
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden h-7 w-7 shrink-0 text-muted-foreground lg:flex"
              onClick={() => onCollapse(true)}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        <Separator />

        <div className="flex flex-1 flex-col min-h-0 px-2 py-2 space-y-1 overflow-y-auto">
          <SidebarLinkRow to="/" label="Home" Icon={Home} exact collapsed={collapsed} largeCollapsedIcon />
          {canDispatch && (
            <SidebarLinkRow to="/eta" label="ETA Tracker" Icon={Navigation} collapsed={collapsed} />
          )}
          {canAnalytics && (
            <SidebarLinkRow to="/analytics" label="Analytics" Icon={BarChart3} collapsed={collapsed} />
          )}
        </div>

        <Separator />
        <SidebarFooter collapsed={collapsed} />
      </aside>
    </>
  )
}

// Bottom of the sidebar: Guide / Admin / Settings / Logout. The Guide and
// Admin rows are permission-gated; each row has a collapsed (icon-only) and
// full form. API & MCP references live under Settings → Developer Options.
function SidebarFooter({ collapsed }: Readonly<{ collapsed: boolean }>) {
  const auth = useAuth()
  const canGuide = usePermission('guide:read')
  const isAdmin = usePermission('admin')
  return (
    <div className="p-2 space-y-1">
      {canGuide && (
        <SidebarLinkRow to="/guide/getting-started" label="Guide" Icon={BookOpen} collapsed={collapsed} muted />
      )}
      {isAdmin && (
        <SidebarLinkRow to="/admin/users" label="Admin" Icon={ShieldCheck} collapsed={collapsed} muted />
      )}
      <SidebarLinkRow to="/settings" label="Settings" Icon={Settings} collapsed={collapsed} muted />
      {auth.isAuthenticated && (
        <SidebarLogoutRow collapsed={collapsed} onClick={() => auth.signoutRedirect()} />
      )}
    </div>
  )
}

// Shared row. The collapsed form wraps the link in a Tooltip so the label
// still surfaces on hover. `exact` keeps Home from matching every route.
function SidebarLinkRow({
  to,
  label,
  Icon,
  collapsed,
  muted = false,
  exact = false,
  largeCollapsedIcon = false,
}: Readonly<{
  to: LinkProps['to']
  label: string
  Icon: LucideIcon
  collapsed: boolean
  muted?: boolean
  exact?: boolean
  largeCollapsedIcon?: boolean
}>) {
  const matches = useMatches()
  const active = isNavLinkActive(matches.map((m) => m.fullPath), to as string, exact)
  const baseExpanded = cn(
    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    muted && 'text-muted-foreground',
  )
  const baseCollapsed = cn(
    'flex items-center justify-center rounded-md p-2 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    muted && 'text-muted-foreground',
  )
  const activeClass = active ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' : ''
  const collapsedIconSize = largeCollapsedIcon ? 'h-4 w-4' : 'h-3.5 w-3.5'
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={to} className={cn(baseCollapsed, activeClass)}>
            <Icon className={collapsedIconSize} />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    )
  }
  return (
    <Link to={to} className={cn(baseExpanded, activeClass)}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </Link>
  )
}

function SidebarLogoutRow({
  collapsed,
  onClick,
}: Readonly<{ collapsed: boolean; onClick: () => void }>) {
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="mx-auto flex h-7 w-7 text-muted-foreground" onClick={onClick}>
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">Log out</TooltipContent>
      </Tooltip>
    )
  }
  return (
    <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground" onClick={onClick}>
      <LogOut className="h-3.5 w-3.5" />
      Logout
    </Button>
  )
}
